import { call, put, all, takeLatest } from 'redux-saga/effects';
import {
    ActivityResponse,
    IDeleteActivityLike,
    IGetFeed,
    IGetFeedComment,
    IGetFeedResponse,
    IGetFeedResponseForS3,
    IGetPersistentFeed,
    ILikeActivity,
    ISaveSearchActivityPayload,
    IWarmTimeline,
    SearchSuggestionResponse,
    types,
} from './types';
import {
    activityUrl,
    commentUrl,
    eventsUrl,
    getContestUrl,
    getSuggestionsUrl,
    likeUrl,
    projectUrl,
    thougthUrl,
} from '../../configurations/api/url';
import { httpRequest } from '../types';
import { api } from '../../configurations/api';
import {
    deleteActivityLikeFail,
    deleteActivityLikeSuccess,
    getMyActivitiesSuccess,
    getMyActivitiesFail,
    likeActivityFail,
    likeActivitySuccess,
    saveSearchActivity,
    saveSearchActivitFail,
    getSearchSuggestionsSuccess,
    getSearchSuggestionsFail,
    hideMyActivitySuccess,
    hideMyActivityFail,
    getActivityFeedSuccess,
    getActivityFeedFail,
    getActivityFeed as getActivityFeedAction,
    getFeedSuggestionSuccess,
    getFeedSuggestionFail,
    getFeedCommentsSuccess,
    getFeedCommentsFail,
    getPersistentFeedSuccess,
    warmTimelineSuccess,
    warmTimelineFail,
} from './actions';
import { parseGenericCollection, parseSingleActivity, parseSingleSuggestion } from '../../utils/responseProcessor';
import { getCacheByType, getCacheDuration, getNotUniqueCacheByKey, isUseCacheEnabled } from '../cache/saga';
import { CACHE_TYPE, CacheValue, NotUniqueCacheValue } from '../cache/types';
import { destroyOneCache, setCache } from '../cache/action';
import { IGenericDocumentModifier } from '../../types/global/helper';
import { Action } from 'redux';
import { SentryCapture } from '../../analytics/Sentry';
import { isEmpty } from '../../utils/lodash';
import moment from 'moment';
import { globalErrorHandler } from '../error/saga';
import { genericParseSingleDocument } from '../../utils/responseProcessor';
import _ from 'lodash';

function* warmTimeline({ payload }: { payload: IWarmTimeline }): any {
    try {
        yield call(api, `${activityUrl}/feed`, httpRequest.POST, payload);
        yield put(warmTimelineSuccess());
    } catch (error: any) {
        yield put(warmTimelineFail(error));
    }
}

//TODO Handle internal cache here
function* getFeedItemComments({ payload }: { payload: IGetFeedComment }): Generator<any, any, any> {
    let initialResult: any = null;
    const queryParams = new URLSearchParams();
    if (payload.limit) queryParams.append('limit', String(payload.limit));
    if (payload.page) queryParams.append('page', String(payload.page));
    if (payload.category) queryParams.append('category', payload.category);
    if (payload.categoryId) queryParams.append('categoryId', payload.categoryId);
    if (payload.parent_comment) queryParams.append('parent_comment', payload.parent_comment);
    const defaultUseCache = yield* isUseCacheEnabled();
    const cache: NotUniqueCacheValue = yield* getNotUniqueCacheByKey(
        CACHE_TYPE.ACTIVITY_FEED_COMMENT,
        queryParams.toString(),
    );
    if (cache && cache.key) {
        initialResult = cache.value;
    }

    if (defaultUseCache && initialResult) {
        yield put(getFeedCommentsSuccess({ ...initialResult, parent_comment: payload.parent_comment }));
    } else {
        try {
            const request = `${commentUrl}/feed?${queryParams.toString()}`;
            const response = yield call(api, `${request}`, httpRequest.GET, null, 2, 1000);
            yield put(getFeedCommentsSuccess({ ...response, parent_comment: payload.parent_comment }));
        } catch (error: any) {
            yield put(getFeedCommentsFail(error));
            yield call(globalErrorHandler, error);
            SentryCapture(error);
        }
    }
}
function* getFeedSuggestion(): Generator<any, any, any> {
    const request = `${activityUrl}/suggestion`;
    let initialResult: any = null;
    const defaultUseCache = yield* isUseCacheEnabled();
    const defaultCacheDuration = yield* getCacheDuration();
    const cache: CacheValue = yield* getCacheByType(CACHE_TYPE.FEED_SUGGESTION);

    if (
        cache &&
        cache.key &&
        !isEmpty(cache.value) &&
        ((Date.now() - Number(cache.key)) as unknown as number) < defaultCacheDuration // To Temporarily fetch from server directly add `&& false` here to override cache
    ) {
        initialResult = cache.value;
    }
    if (initialResult && initialResult.length > 0 && defaultUseCache) {
        yield put(getFeedSuggestionSuccess(initialResult));
    } else {
        try {
            const response = yield call(api, request, httpRequest.GET, {}, 3, 1000);
            const { data } = response;
            yield put(getFeedSuggestionSuccess(data));
            yield put(setCache({ key: Date.now(), value: data, type: CACHE_TYPE.FEED_SUGGESTION, isUnique: true }));
        } catch (error: any) {
            SentryCapture(error);
            yield put(getFeedSuggestionFail(error));
        }
    }
}

function* fetchJsonFromS3Url(url: string): Generator<any, any, any> {
    try {
        const response = yield call(fetch, url);
        if (!response.ok) throw new Error(`Failed to fetch ${url}`);
        const json = yield call([response, 'json']);
        return json;
    } catch (err) {
        SentryCapture(err);
        return [];
    }
}

function* getActivityFeedS3({ payload }: { payload: IGetPersistentFeed }): Generator<any, any, any> {
    const { isLoadMore = false } = payload || {};

    // Fisher-Yates shuffle
    const shuffle = (array: any[]) => {
        return _.shuffle(payload);
    };

    let initialResult: any = null;
    // const defaultUseCache = yield* isUseCacheEnabled();
    const defaultUseCache = true;
    const cache: CacheValue = yield* getCacheByType(CACHE_TYPE.ACTIVITY_FEED);
    const defaultCacheDuration = yield* getCacheDuration();

    if (cache && cache.key && ((Date.now() - Number(cache.key)) as unknown as number) < defaultCacheDuration) {
        initialResult = cache.value as IGetFeedResponse;
    }

    if (defaultUseCache && initialResult) {
        const shuffledResult: IGetFeedResponse = {
            filters: initialResult.filters || [],
            chronological: isLoadMore ? shuffle(initialResult.chronological || []) : initialResult.chronological || [],
            following: isLoadMore ? shuffle(initialResult.following || []) : initialResult.following || [],
            interest: isLoadMore ? shuffle(initialResult.interest || []) : initialResult.interest || [],
        };

        yield put(getPersistentFeedSuccess(shuffledResult));
    } else {
        const request = `${activityUrl}/feed-s3-urls`;
        try {
            const response = yield call(api, request, httpRequest.GET, {}, 2, 2000);
            const { data }: { data: IGetFeedResponseForS3 } = response;
            const { filters } = data.filterUrls;
            const { chronological, following, interest } = data.timelineUrls;

            const [chronologicalFeedRaw, followingFeedRaw, interestFeedRaw, filtersFeed] = yield all([
                chronological ? call(fetchJsonFromS3Url, chronological) : null,
                following ? call(fetchJsonFromS3Url, following) : null,
                interest ? call(fetchJsonFromS3Url, interest) : null,
                filters ? call(fetchJsonFromS3Url, filters) : null,
            ]);

            const chronologicalFeed =
                isLoadMore && chronologicalFeedRaw ? shuffle(chronologicalFeedRaw) : chronologicalFeedRaw || [];
            const followingFeed = isLoadMore && followingFeedRaw ? shuffle(followingFeedRaw) : followingFeedRaw || [];
            const interestFeed = isLoadMore && interestFeedRaw ? shuffle(interestFeedRaw) : interestFeedRaw || [];

            const fullFeed: IGetFeedResponse = {
                filters: filtersFeed || [],
                chronological: chronologicalFeed,
                following: followingFeed,
                interest: interestFeed,
            };

            yield put(getPersistentFeedSuccess(fullFeed));

            yield put(
                setCache({
                    key: Date.now(),
                    value: fullFeed,
                    type: CACHE_TYPE.ACTIVITY_FEED,
                    isUnique: false,
                }),
            );
        } catch (error: any) {
            SentryCapture(error);
            yield put(getActivityFeedFail({ ...error, additionalPayload: payload }));
        }
    }
}

function* getActivityFeed({ payload }: { payload: IGetFeed }): Generator<any, any, any> {
    const queryParams = new URLSearchParams();
    // Conditionally add parameters
    if (payload.limit) queryParams.append('limit', String(payload.limit));
    if (payload.page) queryParams.append('page', String(payload.page));
    if (payload.category) queryParams.append('category', payload.category);
    if (payload.activity_type && payload.activity_type !== 'none')
        queryParams.append('activity_type', payload.activity_type);
    /** This allows us see what has been already seen */
    if (payload.show_seen) queryParams.append('show_seen', String(payload.show_seen));
    if (payload.startDate) queryParams.append('startDate', String(payload.startDate));
    if (payload.endDate) queryParams.append('endDate', String(payload.endDate));
    if (payload.cursor) queryParams.append('cursor', String(JSON.stringify(payload.cursor)));
    let initialResult: any = null;
    const defaultUseCache = yield* isUseCacheEnabled();
    const cache: NotUniqueCacheValue = yield* getNotUniqueCacheByKey(CACHE_TYPE.ACTIVITY_FEED, queryParams.toString());
    if (cache && cache.key) {
        initialResult = cache.value as IGetFeedResponse;
    }
    if (defaultUseCache && initialResult) {
        yield put(getActivityFeedSuccess({ ...initialResult, older: payload.older }));
    } else {
        const request = `${activityUrl}/feed?${queryParams.toString()}`;
        try {
            const response = yield call(api, request, httpRequest.GET, {}, 2, 2000);
            const { data }: { data: IGetFeedResponse } = response;
            yield put(getActivityFeedSuccess({ ...data, older: payload.older }));
            const { following, interest, pagination } = data;
            const { category } = pagination!;
            if (category === 'following' && following && following?.length < 10) {
                // Trigger interests
                yield put(
                    getActivityFeedAction({
                        category: 'interest',
                        page: 1,
                        startDate: moment().toISOString(),
                        endDate: moment().subtract(7, 'days').toISOString(),
                        cursor: null,
                    }),
                );
            }
            if (category === 'interest' && interest && interest.length < 10) {
                // Trigger interests
                yield put(
                    getActivityFeedAction({
                        category: 'chronological',
                        page: 1,
                        cursor: null,
                    }),
                );
            }

            yield put(
                setCache({
                    key: queryParams.toString(),
                    value: data,
                    type: CACHE_TYPE.ACTIVITY_FEED,
                    isUnique: false,
                }),
            );
        } catch (error: any) {
            SentryCapture(error);
            yield put(getActivityFeedFail({ ...error, additionalPayload: payload }));
        }
    }
}

function* hideMyActivity({ payload }: { payload: IGenericDocumentModifier }): any {
    try {
        const response = yield call(
            api,
            `${activityUrl}/${payload.documentId}`,
            httpRequest.PATCH,
            payload.payload,
            0,
            0,
        );
        const { data }: { data: ActivityResponse } = response.data;
        yield put(hideMyActivitySuccess(data));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.ACTIVITY }));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(hideMyActivityFail(error));
    }
}
function* getMyActivities(): any {
    const defaultUseCache = yield* isUseCacheEnabled();
    const defaultCacheDuration = yield* getCacheDuration();
    let initialResult: any = null;
    const cache: CacheValue = yield* getCacheByType(CACHE_TYPE.ACTIVITY);
    if (
        cache &&
        cache.key &&
        !isEmpty(cache.value) &&
        ((Date.now() - Number(cache.key)) as unknown as number) < defaultCacheDuration // To Temporarily fetch from server directly add `&& false` here to override cache
    ) {
        initialResult = cache.value;
    }

    if (initialResult && initialResult.length > 0 && defaultUseCache) {
        yield put(getMyActivitiesSuccess(initialResult));
    } else {
        try {
            const response = yield call(api, activityUrl, httpRequest.GET, null, 0, 0);
            const { data } = response.data;
            const parsedActivitied = parseGenericCollection(data, parseSingleActivity);
            yield put(getMyActivitiesSuccess(parsedActivitied));
            yield put(
                setCache({ key: Date.now(), value: parsedActivitied, type: CACHE_TYPE.ACTIVITY, isUnique: true }),
            );
        } catch (error: any) {
            SentryCapture(error, 'error');
            yield put(getMyActivitiesFail(error));
        }
    }
}

function* saveActivity({ payload }: { payload: ISaveSearchActivityPayload }): any {
    try {
        const response = yield call(api, activityUrl, httpRequest.POST, payload, 0, 0);
        const { data } = response;
        yield put(saveSearchActivity(data));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(saveSearchActivitFail(error));
    }
}

function* getSearchSuggestions(): any {
    const defaultUseCache = yield* isUseCacheEnabled();
    const defaultCacheDuration = yield* getCacheDuration();
    let initialResult: any = null;
    const cache: CacheValue = yield* getCacheByType(CACHE_TYPE.SEARCH_SUGGESTION);
    if (
        cache &&
        cache.key &&
        !isEmpty(cache.value) &&
        ((Date.now() - Number(cache.key)) as unknown as number) < defaultCacheDuration
    ) {
        initialResult = cache.value;
    }

    if (initialResult && defaultUseCache) {
        yield put(getSearchSuggestionsSuccess(initialResult));
    } else {
        try {
            const response: SearchSuggestionResponse = yield call(api, getSuggestionsUrl, httpRequest.GET, null, 0, 0);
            const { data } = response;
            const parseSuggestion = parseGenericCollection(data, parseSingleSuggestion);
            yield put(getSearchSuggestionsSuccess(parseSuggestion));
            yield put(
                setCache({
                    key: Date.now(),
                    value: parseSuggestion,
                    type: CACHE_TYPE.SEARCH_SUGGESTION,
                    isUnique: true,
                }),
            );
        } catch (error: any) {
            SentryCapture(error, 'error');
            yield put(getSearchSuggestionsFail(error));
        }
    }
}

function* likeAnActivity({ payload }: { payload: ILikeActivity }): any {
    try {
        const { like_category, like_type, activityId, like_origin } = payload;
        let request = `${thougthUrl}/${activityId}/like`;

        // Set request based on category
        if (like_category === 'contest') {
            request = `${getContestUrl}/${activityId}/like`;
        } else if (like_category === 'thougth') {
            request = `${thougthUrl}/${activityId}/like`;
        } else if (like_category === 'event') {
            request = `${eventsUrl}/${activityId}/like`;
        } else if (like_category === 'project') {
            request = `${projectUrl}/${activityId}/like`;
        }

        const response = yield call(api, request, httpRequest.POST, { like_category, like_type }, 0, 2000, false);
        const { data } = response.data;
        const parsedLike = genericParseSingleDocument(data);
        yield put(likeActivitySuccess({ data: parsedLike, like_origin: like_origin }));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(
            likeActivityFail({
                activityId: payload.activityId,
                like_category: payload.like_category,
                ...error,
            }),
        );
    }
}

function* deleteAnActivityLike({ payload }: { payload: IDeleteActivityLike }): any {
    try {
        const { likeId, activityId } = payload;
        const request = `${likeUrl}/${likeId}`;
        yield call(api, request, httpRequest.DELETE, {}, 2, 2000, false);
        yield put(
            deleteActivityLikeSuccess({
                data: payload as any,
                documentId: activityId,
                like_origin: payload.like_origin,
            }),
        );
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(
            deleteActivityLikeFail({
                activityId: payload.activityId,
                like_category: payload.like_category,
                additionalPayload: payload.additionalPayload,
                ...error,
            }),
        );
    }
}

/** Watchers */

interface TaskAction extends Action {
    payload: any;
}

function* getMyActivitiesWatcher() {
    yield takeLatest(types.GET_MY_ACTIVITIES, getMyActivities);
}

function* saveActivityWatcher() {
    yield takeLatest<any>(types.SAVE_ACTIVITY, saveActivity);
}

function* getSearchSuggestionsWatcher() {
    yield takeLatest(types.GET_SEARCH_SUGGESTIONS, getSearchSuggestions);
}

function* hideActivityWatcher() {
    yield takeLatest<TaskAction>(types.HIDE_MY_ACTIVITY, hideMyActivity);
}

function* getActivityFeedWatcher() {
    yield takeLatest<TaskAction>(types.GET_ACTIVITY_FEED, getActivityFeed);
}

function* getPersistentActivityFeedWatcher() {
    yield takeLatest<TaskAction>(types.GET_PERSISTENT_FEED, getActivityFeedS3);
}

function* getFeedSuggestionWatcher() {
    yield takeLatest<TaskAction>(types.GET_FEED_SUGGESTION, getFeedSuggestion);
}
function* getFeedCommentsWatcher() {
    yield takeLatest<TaskAction>(types.GET_FEED_COMMENT, getFeedItemComments);
}

function* likeActivityWatcher() {
    yield takeLatest<TaskAction>(types.LIKE_ACTIVITY, likeAnActivity);
}

function* deleteActivityLikeWatcher() {
    yield takeLatest<TaskAction>(types.DELETE_ACTIVITY_LIKE, deleteAnActivityLike);
}

function* warmTimelineWatcher() {
    yield takeLatest<TaskAction>(types.WARM_TIMELINE, warmTimeline);
}

export default function* accountSaga() {
    yield all([
        getMyActivitiesWatcher(),
        saveActivityWatcher(),
        getSearchSuggestionsWatcher(),
        hideActivityWatcher(),
        getActivityFeedWatcher(),
        getFeedSuggestionWatcher(),
        getFeedCommentsWatcher(),
        likeActivityWatcher(),
        deleteActivityLikeWatcher(),
        getPersistentActivityFeedWatcher(),
        warmTimelineWatcher(),
    ]);
}
